import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useMetadataForElements } from "@/hooks/useMetadataForElements";
import { cn } from "@/lib/utils";
import { setDraggingComponent } from "@/store/iframeSlice";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { useUpdateComponentPreview } from "@/utils/componentPreviewUtils";
import { getLookupKeyForComponent } from "@/utils/jsx/extractElementsUtils";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { compressNavTree } from "@/utils/navTreeCompression";
import { NavTreeNode } from "@/utils/types";
import { useCanvasId } from "@/utils/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpRight,
  Blocks,
  Grid2X2,
  LayoutPanelLeft,
  LayoutPanelTop,
  PictureInPicture,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { getContentsWithoutShell } from "../Canvas/IframePool/utils";
import { setTempoClipboardDataOnDrag } from "../Canvas/utils/canvasClipboardData";
import { ControlEditor } from "../ControlEditor";
import { Button } from "../ui/button";
import { AssetIframe } from "./AssetPreview/AssetIframe";
import { getAssetNavTreeWithoutShell } from "./AssetPreview/utils";
import { useDragImage, useStoryboardControls } from "./utils";

interface DetailsPanelProps {
  component: JsxElement;
  variants: JsxElement[];
  onClose: () => void;
  onAddToCanvas: (
    variant?: JsxElement,
    propsToSet?: { [key: string]: any },
  ) => void;
}

// Define variants and transition outside the return statement for clarity
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? "100%" : "-100%",
    opacity: 0,
  }),
};

const slideTransition = {
  x: { type: "tween" as const },
  opacity: { duration: 0.2 },
};

export const DetailsPanel = React.memo(
  ({ component, variants, onClose, onAddToCanvas }: DetailsPanelProps) => {
    const [currentVariantIndex, setCurrentVariantIndex] = useState<number>(0);
    const totalItems = variants.length > 0 ? variants.length : 1;
    const currentItem =
      variants.length > 0 ? variants[currentVariantIndex] : component;
    const [direction, setDirection] = useState(0); // 0 = initial, 1 = next, -1 = prev
    const [PlaceholderIcon, setPlaceholderIcon] = useState<LucideIcon | null>(
      null,
    );

    // Track image loading states locally to avoid flicker
    const [imageLoadStates, setImageLoadStates] = useState<{
      [key: string]: "loading" | "loaded" | "error";
    }>({});

    // Get image previews for the main asset and all variants
    const mainAssetImage = useMetadataForElements([component])[0];
    const variantImages = useMetadataForElements(variants);

    // Get the current image and loading state to display
    const currentImageData =
      variants.length > 0 ? variantImages[currentVariantIndex] : mainAssetImage;

    const [capturePreview, setCapturePreview] = useState(false);

    const currentImage = currentImageData.imagePath;
    const currentImageKey = `${currentItem.codebaseId}_${currentVariantIndex}_${currentImage}`;

    // Use consistent motion key based only on variant index, not content type
    const motionKey = `variant-${currentVariantIndex}`;

    useEffect(() => {
      setCurrentVariantIndex(0);
      setImageLoadStates({});
    }, [component.key]);

    // Initialize loading state when image URL appears
    useEffect(() => {
      if (currentImage && !(currentImageKey in imageLoadStates)) {
        setImageLoadStates((prev) => ({
          ...prev,
          [currentImageKey]: "loading",
        }));
      }
    }, [currentImage, currentImageKey, imageLoadStates]);

    useEffect(() => {
      if (!currentImage || imageLoadStates[currentImageKey] === "error") {
        setCapturePreview(true);
      }
    }, [imageLoadStates, currentImage, currentImageKey]);

    const previewContainerRef = useRef<HTMLDivElement>(null);
    const latestOuterHtmlRef = useRef<string>();
    const latestNavTreeRef = useRef<NavTreeNode>();
    const latestImgRef = useRef<{
      light: Blob;
      dark: Blob;
      width: number;
      height: number;
    } | null>(null);

    const handleImageLoad = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "loaded" }));
    }, []);

    const handleImageError = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "error" }));
    }, []);

    const handleImageLoadStart = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "loading" }));
    }, []);

    // Show placeholder if no image URL or if the image is confirmed broken
    const shouldShowPlaceholder =
      !currentImage || imageLoadStates[currentImageKey] === "error";

    // Show loading spinner only if we have an image URL and haven't tested it yet
    const isImageLoading =
      currentImage &&
      !(currentImageKey in imageLoadStates) &&
      !shouldShowPlaceholder;

    const wheelDelta = useRef(0);
    const lastWheelTime = useRef(0);
    const isTrackpad = useRef<boolean | null>(null);

    const { setupDragImage, cleanupDragImage, trueDimensions } = useDragImage(
      currentImage || undefined,
      currentImageData.width || undefined,
      currentImageData.height || undefined,
    );

    const handleNextVariant = useCallback(() => {
      setDirection(1);
      setCurrentVariantIndex((prevIndex) =>
        Math.min(prevIndex + 1, totalItems - 1),
      );
    }, [totalItems]);

    const handlePrevVariant = useCallback(() => {
      setDirection(-1);
      setCurrentVariantIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    }, []);

    const handleWheel = useCallback(
      (e: React.WheelEvent) => {
        // Only handle scroll if there are multiple variants
        if (variants.length <= 1) return;

        e.stopPropagation();

        const now = performance.now();
        const timeDelta = now - lastWheelTime.current;
        lastWheelTime.current = now;

        // Detect input device based on timing and delta
        if (isTrackpad.current === null) {
          // Trackpads typically have small deltas (< 50) and quick succession (< 50ms)
          isTrackpad.current = Math.abs(e.deltaY) < 50 && timeDelta < 50;
        }

        // Different thresholds for different input devices
        const threshold = isTrackpad.current ? 100 : 0;

        // Accumulate scroll delta
        wheelDelta.current += Math.abs(e.deltaY);

        // Only trigger variant change when threshold is reached
        if (wheelDelta.current >= threshold) {
          if (e.deltaY > 0) {
            handleNextVariant();
          } else {
            handlePrevVariant();
          }
          wheelDelta.current = 0; // Reset accumulator
        }

        // Reset device detection after a pause in scrolling
        if (timeDelta > 1000) {
          isTrackpad.current = null;
        }
      },
      [variants.length, handleNextVariant, handlePrevVariant],
    );
    const setCanvasComponentKey = useCanvasStore(
      (store) => store.setCanvasComponentKey,
    );

    const handleLinkOut = () => {
      setCanvasComponentKey(getLookupKeyForComponent(component));

      onClose();
    };

    const dispatch = useDispatch();
    const canvasId = useCanvasId();

    // Select random placeholder icon on mount
    useEffect(() => {
      const placeholderIcons: LucideIcon[] = [
        Blocks,
        Grid2X2,
        LayoutPanelTop,
        LayoutPanelLeft,
      ];
      setPlaceholderIcon(
        () =>
          placeholderIcons[Math.floor(Math.random() * placeholderIcons.length)],
      );
    }, []);

    const {
      controls,
      values,
      loading,
      error,
      handleChange,
      handleSetCurrentValues,
      changed,
    } = useStoryboardControls(currentItem);
    const hasControls = controls && Object.keys(controls).length > 0;

    const handleDomSnapshot = useCallback(
      ({
        outerHtml,
        navTree,
      }: {
        outerHtml?: string;
        navTree?: NavTreeNode | null;
      }) => {
        if (outerHtml) {
          latestOuterHtmlRef.current = outerHtml;
        }
        if (navTree) {
          latestNavTreeRef.current = navTree;
        }
      },
      [],
    );

    const handleDragStart = useCallback(
      (e: React.DragEvent, variant: JsxElement) => {
        e.dataTransfer.dropEffect = "move";
        if (!variant.codebaseId) {
          toast.error("Could not find element in codebase");
          return;
        }

        if (!variant.storybookSourceStart) {
          toast.error("Could not parse storybook source code");
          return;
        }

        dispatch(setDraggingComponent(variant.type));
        setTempoClipboardDataOnDrag(e, {
          htmlForInstantUpdate: getContentsWithoutShell(
            latestOuterHtmlRef.current || "",
          ),
          compressedNavTreeNodesForInstantUpdate: (latestNavTreeRef.current
            ? getAssetNavTreeWithoutShell(latestNavTreeRef.current)
            : []
          )
            .map((result) => compressNavTree(result))
            .filter((result) => result !== null),
          insertCodebaseId: variant.codebaseId,
          insertIntoHoveredElement: true,
          width: currentImageData.width || trueDimensions.width || 0,
          height: currentImageData.height || trueDimensions.height || 0,
          propsToSet: values,
        });

        // Read zoomPerc imperatively at drag time (no subscription)
        const zoomPerc =
          useCanvasStore.getState().canvasIdToPos[canvasId]?.zoomPerc || 1;
        setupDragImage(e, zoomPerc);
      },
      [
        dispatch,
        canvasId,
        setupDragImage,
        trueDimensions.height,
        trueDimensions.width,
        currentImageData.width,
        currentImageData.height,
        values,
      ],
    );

    const updateComponentPreview = useUpdateComponentPreview();

    const handleUpdateComponentPreview = useCallback(() => {
      if (
        latestImgRef.current &&
        latestOuterHtmlRef.current &&
        latestNavTreeRef.current
      ) {
        updateComponentPreview(
          currentItem,
          latestImgRef.current,
          getContentsWithoutShell(latestOuterHtmlRef.current || ""),
          getAssetNavTreeWithoutShell(latestNavTreeRef.current)[0],
          latestImgRef.current.width,
          latestImgRef.current.height,
        );
      }
    }, [currentItem, updateComponentPreview]);

    const handleImgGenerated = useCallback(
      (
        data: { light: Blob; dark: Blob; width: number; height: number } | null,
      ) => {
        latestImgRef.current = data;
        setCapturePreview(false);
        handleUpdateComponentPreview();
      },
      [handleUpdateComponentPreview],
    );

    const canvasDarkMode = useDarkModeStore(
      (state) => state.canvasDarkModeStates[canvasId] || false,
    );

    return (
      <div className="w-[200px] h-full flex flex-col bg-background-primary border border-border-primary shrink-0 shadow-xl sticky rounded-lg">
        {/* Header */}
        <div className="flex items-center p-2 pl-4 border-b border-secondary-300 shrink-0 gap-1">
          <h2 className="text-xs font-medium text-text-primary grow">
            Details
          </h2>
          <button
            data-tooltip-id="tooltip"
            data-tooltip-place="top"
            data-tooltip-content="Save Preview"
            type="button"
            onClick={() => setCapturePreview(true)}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Save Preview"
          >
            <PictureInPicture className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Preview Area */}
        <div className="p-4 grow flex flex-col min-h-0">
          <div
            className={cn(
              "rounded-lg",
              canvasDarkMode ? "bg-secondary-200" : "bg-secondary-n-50",
            )}
          >
            <div
              ref={previewContainerRef}
              className="relative rounded-lg aspect-square flex items-center justify-center p-1 overflow-hidden h-full w-full group cursor-pointer"
              draggable={true}
              onDragStart={(e) => handleDragStart(e, currentItem)}
              onDragEnd={() => {
                cleanupDragImage();
                dispatch(setDraggingComponent(null));
              }}
              onWheel={handleWheel}
            >
              {/* Display the current image */}
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={motionKey}
                  custom={direction}
                  variants={slideVariants}
                  initial={"enter"}
                  animate="center"
                  exit="exit"
                  transition={slideTransition}
                  className="absolute inset-0 flex items-center justify-center p-1"
                >
                  <AssetIframe
                    capture={capturePreview}
                    onImgGenerated={handleImgGenerated}
                    className="mx-4 w-full h-full"
                    component={currentItem}
                    storyArgs={values}
                    onStoryArgsUpdated={handleSetCurrentValues}
                    onDomSnapshot={handleDomSnapshot}
                    fallback={
                      <>
                        {currentImage &&
                        imageLoadStates[currentImageKey] === "loaded" ? (
                          <img
                            key={currentImage}
                            src={currentImage}
                            alt={currentItem.componentName}
                            className="object-scale-down max-w-full max-h-full"
                            onLoad={() => handleImageLoad(currentImageKey)}
                            onError={() => handleImageError(currentImageKey)}
                            onLoadStart={() =>
                              handleImageLoadStart(currentImageKey)
                            }
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-text-secondary">
                            {isImageLoading ? (
                              <div className="w-16 h-16 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
                              </div>
                            ) : (
                              PlaceholderIcon && (
                                <PlaceholderIcon className="w-16 h-16 text-gray-400" />
                              )
                            )}
                          </div>
                        )}
                        {currentImage &&
                          imageLoadStates[currentImageKey] !== "loaded" && (
                            <img
                              src={currentImage}
                              alt=""
                              className="hidden"
                              onLoad={() => handleImageLoad(currentImageKey)}
                              onError={() => handleImageError(currentImageKey)}
                              onLoadStart={() =>
                                handleImageLoadStart(currentImageKey)
                              }
                            />
                          )}
                      </>
                    }
                  />
                </motion.div>
              </AnimatePresence>

              {variants.length > 1 && (
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={handlePrevVariant}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-secondary-300/50 hover:bg-secondary-300/80 rounded-md text-text-primary disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
                    disabled={currentVariantIndex === 0}
                  >
                    <ArrowLeftIcon className="p-1 w-6 h-6 rounded-sm bg-background-primary text-text-primary shadow-2xs pointer-events-auto visible hover:bg-background-secondary active:bg-background-secondary focus-visible:ring-4 focus-visible:ring-[hsl(var(--blue-60)/50%)] transition-colors ease-out" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextVariant}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-secondary-300/50 hover:bg-secondary-300/80 rounded-md text-text-primary disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
                    disabled={currentVariantIndex === totalItems - 1}
                  >
                    <ArrowRightIcon className="p-1 w-6 h-6 rounded-sm bg-background-primary text-text-primary shadow-2xs pointer-events-auto visible hover:bg-background-secondary active:bg-background-secondary focus-visible:ring-4 focus-visible:ring-[hsl(var(--blue-60)/50%)] transition-colors ease-out" />
                  </button>

                  <div className="absolute top-4 right-4 z-10 flex space-x-1.5 pointer-events-auto">
                    {Array.from({ length: totalItems }).map((_, index) => (
                      <button
                        key={`variant-dot-${component.key}-${index}`}
                        type="button"
                        className={cn(
                          "block w-1.5 h-1.5 rounded-full cursor-pointer",
                          index === currentVariantIndex
                            ? "bg-white"
                            : "bg-[#4B4B51]",
                        )}
                        onClick={() => {
                          // Determine direction based on index change
                          const newDirection =
                            index > currentVariantIndex ? 1 : -1;
                          setDirection(newDirection);
                          setCurrentVariantIndex(index);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 grow flex flex-col min-h-0 border-t border-border-primary">
          {/* Label & Variants */}
          <div className="shrink-0 mb-2">
            <div className="flex items-center text-text-primary font-medium mb-0.5 text-xs gap-1.5">
              {hasControls && (
                <span className="p-[3px] flex items-center justify-center">
                  <img
                    src="/icons/storybook.svg"
                    alt="storybook icon"
                    className="w-3 h-3"
                  />
                </span>
              )}
              <span className="text-wrap w-full wrap-break-word leading-tight">
                {component.componentName || "Component"}
                {variants.length > 0 &&
                  currentVariantIndex < variants.length && (
                    <span className="text-text-secondary">
                      &nbsp;({currentItem.componentName})
                    </span>
                  )}
              </span>
            </div>
            <div className="text-xs text-text-secondary flex items-center">
              <button
                type="button"
                onClick={handleLinkOut}
                className="text-text-secondary hover:text-text-primary py-1 pb-1.5 flex items-center gap-1 text-2xs"
                title="Link out"
              >
                <span>
                  {variants.length > 1
                    ? `${variants.length} Variants`
                    : "View Component"}
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {error ? (
            <div className="text-[10px] text-red-400 mb-4">{error}</div>
          ) : loading && !hasControls ? (
            <div className="text-[10px] text-[#9F9FA4] mb-4">
              Loading story controls...
            </div>
          ) : (
            hasControls && (
              <div className="mb-6">
                <ControlEditor
                  controls={controls}
                  values={values}
                  onChange={handleChange}
                  unsavedKeys={changed}
                />
              </div>
            )
          )}

          <Button
            onClick={() =>
              onAddToCanvas(
                variants.length > 0 ? currentItem : undefined,
                values,
              )
            }
            className="w-full mt-auto bg-primary-0 hover:bg-primary-100 text-text-primary shrink-0 text-xs"
          >
            Add to Canvas
          </Button>
        </div>
      </div>
    );
  },
);

DetailsPanel.displayName = "DetailsPanel";
